import { GridHeaderSelectionChangeEvent, GridPagerSettings } from '@progress/kendo-react-grid';
import { GridSelectableSettings } from '@progress/kendo-react-grid/dist/npm/interfaces/GridSelectableSettings';
import { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useProductColumns from 'src/assets/static/view_products/useProductColumns';
import GridWrapper from 'src/components/GridWrapper';
import { Product } from 'src/types/catalog';
import { useContextSelector } from 'use-context-selector';
import ProductInformationPanel from './ProductInformationPanel';
import ProductToolbarActions from './ProductToolbarActions';
import { ViewCatalogProductsContext } from './ViewCatalogProductsContext';

const selectable: GridSelectableSettings = {
  enabled: true,
  drag: false,
  cell: false,
  mode: 'multiple',
};

const gridPagerSettings: GridPagerSettings = {
  buttonCount: 5,
  pageSizes: [10, 25, 50, 100],
};

const defaultProps = {
  tableTextId: 'Products',
  DataItemKey: 'productKey',
  sortable: true,
  resizable: true,
  showColumnMenu: true,
  selectable: selectable,
  pageable: gridPagerSettings,
} as const;

const ProductListView: FC = () => {
  const { t } = useTranslation();
  const dataState = useContextSelector(ViewCatalogProductsContext, (state) => state.dataState);

  const onDataStateChange = useContextSelector(
    ViewCatalogProductsContext,
    (state) => state.onDataStateChange
  );
  const onSelectionChange = useContextSelector(
    ViewCatalogProductsContext,
    (state) => state.onSelectionChange
  );
  const onItemChange = useContextSelector(
    ViewCatalogProductsContext,
    (state) => state.onItemChange
  );
  const productSelections = useContextSelector(
    ViewCatalogProductsContext,
    (state) => state.productSelections
  );
  const setProductSelections = useContextSelector(
    ViewCatalogProductsContext,
    (state) => state.setProductSelections
  );
  const productsData = useContextSelector(ViewCatalogProductsContext, (state) => state.products);
  const productsTotal = useContextSelector(
    ViewCatalogProductsContext,
    (state) => state.productsTotal
  );
  const loadingProducts = useContextSelector(
    ViewCatalogProductsContext,
    (state) => state.loadingProducts
  );
  const isProductPanelOpen = useContextSelector(
    ViewCatalogProductsContext,
    (state) => state.isProductPanelOpen
  );
  const handleCloseProductPanel = useContextSelector(
    ViewCatalogProductsContext,
    (state) => state.handleCloseProductPanel
  );
  const search = useContextSelector(ViewCatalogProductsContext, (state) => state.search);

  // Merge shared selection state with catalog data
  // Use productsData from context
  const catalogData = useMemo(() => {
    if (!productsData) {
      return [];
    }

    const baseData = productsData.map((item: Product) => ({
      selected: false,
      qty: 0,
      ...item,
    }));

    // Merge with shared selection state
    return baseData.map((item) => {
      const selection = productSelections[item.productKey] || { selected: false, qty: 0 };
      return {
        ...item,
        selected: selection.selected,
        qty: selection.qty,
      };
    });
  }, [productsData, productSelections]);

  const maxNumberOfAttributes = useMemo(
    () => Math.max(...productsData.map((product) => product.attributes.length)),
    [productsData]
  );

  const columns = useProductColumns(maxNumberOfAttributes);

  // Wrapper for header selection change that only selects items on the current page
  const handleHeaderSelectionChange = useCallback(
    (event: GridHeaderSelectionChangeEvent) => {
      const checkboxElement: any = event?.syntheticEvent?.target;
      const { checked } = checkboxElement;

      // Only select/deselect items that are currently displayed on this page
      // catalogData already contains only the current page's items (server-side pagination)
      setProductSelections((prev: Record<string, { selected: boolean; qty: number }>) => {
        const updated = { ...prev };
        catalogData.forEach((item) => {
          const { productKey } = item as Product;
          const current = prev[productKey] || { selected: false, qty: 0 };
          updated[productKey] = {
            selected: checked,
            qty: checked && current.qty === 0 ? 1 : checked ? current.qty : 0,
          };
        });
        return updated;
      });
    },
    [catalogData, setProductSelections]
  );

  const hasSearchValue = search && search.trim();

  return (
    <main className="flex h-full min-h-0 flex-col px-4 pb-4">
      {hasSearchValue && (
        <div className="mb-2 shrink-0 truncate text-sm font-[600]">
          {t('Showing results for')} &quot;{search.trim()}&quot;
        </div>
      )}
      <div className="min-h-0 flex-1 overflow-hidden">
        <GridWrapper
          id="productListTable"
          className="product-list-view h-full min-h-0"
          columns={columns}
          dataSource={catalogData}
          total={productsTotal}
          dataState={dataState}
          onDataStateChange={onDataStateChange}
          height="100%"
          gridToolbarChildren={<ProductToolbarActions />}
          {...defaultProps}
          loading={loadingProducts}
          noRecordText={t('No items to display')}
          onHeaderSelectionChange={handleHeaderSelectionChange}
          onSelectionChange={onSelectionChange}
          onItemChange={onItemChange}
          tableTextId="Items"
        />
      </div>
      <ProductInformationPanel isOpen={isProductPanelOpen} onClose={handleCloseProductPanel} />
    </main>
  );
};

export default ProductListView;
